/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2020 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {IWishlistData, WishlistRequestHandler} from "./requestHandler/WishlistRequestHandler";
import {Wishlist} from "./Wishlist";

class WishlistFactory {
  private existingWishlist: Wishlist | null = null;
  private awaitingWishlistPromise: Promise<IWishlistData | null> | null = null;

  public clearWishlist() {
    this.existingWishlist = null;
  }

  /**
   * Add an item to a (potentially new) wishlist
   * @param shopId
   * @param currencyCode
   * @param visitorId
   * @param itemId
   */
  public async addItemToNewWishlist(
    shopId: number,
    currencyCode: string,
    visitorId: string,
    itemId: number,
  ): Promise<Wishlist> {
    if (this.existingWishlist) {
      return this.existingWishlist;
    }

    if (this.awaitingWishlistPromise) {
      await this.awaitingWishlistPromise;
      if (this.existingWishlist) return this.existingWishlist;
    }

    this.awaitingWishlistPromise = WishlistRequestHandler.postWishlistItem(shopId, currencyCode, visitorId, itemId);
    const rawWishlistData = (await this.awaitingWishlistPromise) as IWishlistData;
    this.existingWishlist = new Wishlist(shopId, currencyCode, visitorId, rawWishlistData);
    this.awaitingWishlistPromise = null;

    return this.existingWishlist;
  }

  /**
   * Get wishlist if present, null if not
   * @param shopId
   * @param currencyCode
   * @param visitorId
   */
  public async getWishlist(shopId: number, currencyCode: string, visitorId: string): Promise<Wishlist | null> {
    if (this.existingWishlist) {
      return this.existingWishlist;
    }

    if (this.awaitingWishlistPromise) {
      await this.awaitingWishlistPromise;

      return this.existingWishlist!;
    }

    this.awaitingWishlistPromise = WishlistRequestHandler.getWishlist(shopId, currencyCode, visitorId);
    const rawWishlistData = await this.awaitingWishlistPromise;
    this.existingWishlist = rawWishlistData ? new Wishlist(shopId, currencyCode, visitorId, rawWishlistData) : null;
    this.awaitingWishlistPromise = null;

    return this.existingWishlist;
  }
}

const wishlistFactory = new WishlistFactory();
export {wishlistFactory as WishlistFactory};
